import React, { useState, useCallback, useEffect, useRef } from "react";
import _ from "lodash";
import {
  _findSelectOption,
  _getBaseUrl,
  _capitalized,
  _getSkipByPage,
  _isFirstRender,
  _getCSRFTokenValue,
  _getTranslation,
  _getPlaceHolderKey,
} from "../../utils/helper";
import api from "../../utils/api";

import { Loader, Pagination } from "../../atoms";
import { CardPublication } from "../../cards";
import {
  Datepicker,
  InputSearch,
  InputSelect,
  MobileFilters,
} from "../../forms";

const INITIAL_VALUES = {
  languages: "",
  region: "",
  themes: "",
  type: "",
  // start_date: format(new Date("2022-06-15"), "yyyy-MM-dd"),
  start_date: "",
  end_date: "",
  search_term: "",
  page: 1,
  tag: "",
};

const INITIAL_SORT = "newest";
const SORTING_OPTIONS = [
  {
    label:
      _getTranslation("publication_date") +
      " (" +
      _getTranslation("newest_to_oldest") +
      ")",
    value: "newest",
  },
  {
    label:
      _getTranslation("publication_date") +
      " (" +
      _getTranslation("oldest_to_newest") +
      ")",
    value: "oldest",
  },
  {
    label: "A - Z",
    value: "alpha-ASC",
  },
  {
    label: "Z - A",
    value: "alpha-DESC",
  },
];

const Publications = (props) => {
  const filterListOrder = ["themes", "languages", "region", "type"];
  const [filterOptions, setFilterOptions] = useState([]);
  const [isSearchBoxFocused, setIsSearchBoxFocused] = useState(false);
  const [values, setValues] = useState(INITIAL_VALUES);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_VALUES);
  const [sortby, setSortby] = useState(INITIAL_SORT);
  const prevSortbyRef = useRef(INITIAL_SORT);
  const [highlightedPosts, setHighlightedPosts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [areFiltersActive, setAreFiltersActive] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const isFirstLoad = _isFirstRender();

  const scrollRef = useRef(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tag_id = urlParams.get("tag_id");
    const tag_name = urlParams.get("tag_name");

    if (tag_id && tag_name) {
      setAppliedFilters({
        ...appliedFilters,
        tag: {
          label: tag_name,
          value: parseInt(tag_id),
        },
      });
      setAreFiltersActive(true);
    } else {
      fetchPosts();
    }
  }, [window.location.search]);

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    if (!isFirstLoad) {
      // sync the values when filters are applied
      setValues(appliedFilters);

      fetchPosts();
    }
  }, [appliedFilters]);

  useEffect(() => {
    if (prevSortbyRef.current !== sortby) {
      setAppliedFilters({ ...appliedFilters, page: 1 });
    }
    prevSortbyRef.current = sortby;
  }, [sortby]);

  const fetchFilters = () => {
    setIsLoadingFilters(true);

    api
      .get("/publication/getsearchfields?lang=" + document.getElementById("currentLangCode").innerHTML)
      .then((res) => {
        setIsLoadingFilters(false);

        const filtersData = res.data;
        const formattedList = [];
        Object.keys(filtersData).map((key) => {
          formattedList.push({
            label: _capitalized(key),
            key: key,
            options: filtersData[key],
          });
        });

        setFilterOptions(formattedList);
      })
      .catch(() => {
        setIsLoadingFilters(false);
      });
  };

  const fetchPosts = async () => {
    setIsLoadingPosts(true);

    const POSTS_PER_PAGE = 21;
    const POSTS_SKIP = _getSkipByPage(appliedFilters.page, POSTS_PER_PAGE);

    let sort_order = "DESC";
    let sort_field = "date";

    if (sortby === "alpha-ASC") {
      sort_order = "ASC";
      sort_field = "title";
    } else if (sortby === "alpha-DESC") {
      sort_order = "DESC";
      sort_field = "title";
    } else if (sortby === "oldest") {
      sort_order = "ASC";
      sort_field = "date";
    }

    const fields = {
      language: appliedFilters.languages,
      type: appliedFilters.type,
      region: appliedFilters.region,
      themes: appliedFilters.themes,
      startdate: appliedFilters.start_date,
      enddate: appliedFilters.end_date,
      search_term: appliedFilters.search_term,
      skip: POSTS_SKIP,
      take: POSTS_PER_PAGE,
      show_all: areFiltersActive ? false : true,
      tag: appliedFilters.tag?.value ?? appliedFilters.tag,
      sort_order: sort_order,
      sort_field: sort_field,
    };

    // sendCustomDimensions(fields);

    const csrfToken = await _getCSRFTokenValue();
    api
      .post("/publication/search", fields, {
        headers: {
          "X-CSRF-Token": csrfToken,
        },
      })
      .then((res) => {
        const TOTAL_POSTS = res.data.totalNodes;
        const TOTAL_PAGES = Math.ceil(TOTAL_POSTS / POSTS_PER_PAGE);

        setIsLoadingPosts(false);
        setHighlightedPosts(res.data.highlights);
        setPosts(res.data.publications);
        setTotalPages(TOTAL_PAGES);

        if (!isFirstLoad) {
          if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
          }
        }
      })
      .catch(() => {
        setIsLoadingPosts(false);
      });
  };

  const getFilterInputs = () =>
    isLoadingFilters ? (
      <Loader />
    ) : (
      <React.Fragment>
        {filterListOrder.map((key) => {
          const foundFilterOptions = filterOptions.find(
            (opt) => opt.key === key
          );
          const isMulti = true;

          return (
            foundFilterOptions && (
              <React.Fragment>
                <InputSelect
                  name={foundFilterOptions.key}
                  // placeholder={foundFilterOptions.label}
                  placeholder={`${_getTranslation(_getPlaceHolderKey(foundFilterOptions.key))}`}
                  options={foundFilterOptions.options}
                  isClearable
                  isSearchable
                  isMulti={isMulti}
                  value={_findSelectOption(
                    foundFilterOptions.options,
                    values[foundFilterOptions.key],
                    isMulti
                  )}
                  onChange={(name, selectedValues) => {
                    setValues({
                      ...values,
                      [foundFilterOptions.key]: selectedValues,
                    });
                  }}
                />
                {key === "type" && (
                  <div className="mb-2">
                    <button
                      className="button-open-modal text-mobileS md:text-desktopS tema-text--link"
                      data-target="#pub-types-modal"
                    // onClick={
                    //   typeof window.openModal === "function" &&
                    //   window.openModal
                    // }
                    >
                      {_getTranslation("What_types_of_publications_are_there")}
                    </button>
                  </div>
                )}
              </React.Fragment>
            )
          );
        })}

        <div className="flex items-start gap-4">
          <label className="mt-4 text-sm w-10">{_getTranslation("from")}</label>
          <Datepicker
            value={values.start_date}
            class="flex-1"
            onChange={(selected) => {
              setValues({
                ...values,
                start_date: selected,
                end_date: null,
              });
            }}
          />
        </div>
        <div className="flex items-start gap-4">
          <label className="mt-4 text-sm w-10">{_getTranslation("to")}</label>
          <Datepicker
            value={values.end_date}
            class={`flex-1 ${_.isEmpty(values.start_date)
              ? "pointer-events-none opacity-50"
              : ""
              }`}
            onChange={(selected) => {
              setValues({
                ...values,
                end_date: selected,
              });
            }}
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-y-2 gap-x-4 lg:gap-x-2 mt-4">
          <button
            className="btn btn-s--pill sm:flex-1"
            onClick={() => {
              pushToGTM(values);
              applyFilters();
            }}
          >
            {_getTranslation("apply_filters")}
          </button>
          {/* {!_.isEqual(values, INITIAL_VALUES) && ( */}
          <button
            className="btn btn-s--pill btn-c--hollow---black sm:flex-1"
            onClick={() => resetFilters()}
          >
            {_getTranslation("reset_filters")}
          </button>
          {/* )} */}
        </div>
      </React.Fragment>
    );

  const removeTag = (tag) => {
    const filterKey = tag?.filterKey;

    if (filterKey) {
      const currentValues = values[filterKey];
      let updatedValues = { ...values };

      if (_.isArray(currentValues)) {
        const newValues = currentValues.filter((item) => item !== tag.value);
        updatedValues[filterKey] = newValues;
      } else if (_.isString(currentValues) || _.isObject(currentValues)) {
        let newValue = "";

        // uncomment this if newValue's default value is not empty string
        // if (filterKey === "search_term") {
        //   newValue = "";
        // }

        updatedValues[filterKey] = newValue;
      }

      // setValues(updatedValues);
      setAppliedFilters(updatedValues);
    }
  };

  const FilterTags = useCallback(() => {
    const selectedTags = [];

    Object.keys(appliedFilters).map((filterKey) => {
      const filterCategory = appliedFilters[filterKey];
      if (!_.isEmpty(filterCategory)) {
        if (_.isArray(filterCategory)) {
          filterCategory.map((item) => {
            let foundOption = false;
            filterOptions.find((filterCatItem) => {
              if (filterCatItem.key === filterKey) {
                foundOption = filterCatItem.options.find(
                  (opt) => opt.value === item
                );
              }
            });

            selectedTags.push({
              label: foundOption?.label || item,
              value: item,
              filterKey,
            });
          });
        } else if (_.isString(filterCategory)) {
          selectedTags.push({
            label: filterCategory,
            filterKey,
          });
        } else if (_.isObject(filterCategory)) {
          selectedTags.push({
            label: filterCategory.label,
            value: filterCategory.value,
            filterKey,
          });
        }
      }
    });

    return !_.isEmpty(selectedTags) ? (
      <div className="filter-tags-wrapper flex flex-wrap justify-center">
        {selectedTags.map((tag) => (
          <button
            key={tag}
            className="filter-tag"
            onClick={() => {
              removeTag(tag);
            }}
          >
            {tag.label}
          </button>
        ))}
        <button
          className="filter-tag filter-tag-reset"
          onClick={() => resetFilters()}
        >
          {_getTranslation("clear_all")}
        </button>
      </div>
    ) : null;
  }, [appliedFilters]);

  const pushToGTM = (values) => {
    let gtmData = {
      event: 'publicationFilterSearch',
    }

    const keys = ['languages', 'region', 'themes', 'type'];

    keys.map((key) => {
      if (values[key].length > 0) {
        let temp = '';
        let count = 0;

        const filter = filterOptions.find((options) => {
          return options.key === key;
        });

        values[key].map((vl) => {
          filter.options.map((option) => {
            if (option.value === vl) {
              // temp.push(option.label);
              if (count > 0) {
                temp = temp + ', ';
              }
              temp = temp + '"' + option.label + '"';
              count++;
            }
          });
        });

        // gtmData[key] = temp.toString();
        gtmData[key] = temp;
      } else {
        gtmData[key] = '';
      }
    });

    console.log('gtmData', gtmData);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(gtmData);
  }

  const applyFilters = (pageNo = 1, isFiltersActive = true) => {
    setAppliedFilters({ ...values, page: pageNo });

    if (isFiltersActive) {
      setAreFiltersActive(true);
    }
  };

  const resetFilters = () => {
    setSortby(INITIAL_SORT);
    setValues(INITIAL_VALUES);
    setAppliedFilters(INITIAL_VALUES);
    setAreFiltersActive(false);
  };

  return (
    <React.Fragment>
      <div className="bg-ideaBackgroundGrey100 py-8">
        <div className="container">
          <div className="sm:w-10/12 lg:w-7/12 mx-auto flex flex-col items-center gap-y-3">
            <h1 className="h1 !mb-1">{props?.data["page-title"]}</h1>
            <form
              className="w-full"
              onSubmit={(e) => {
                e.preventDefault();
                console.log('Search entered', {
                  'event': 'publicationKeywordSearch',
                  'keyword': values.search_term,
                });
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  'event': 'publicationKeywordSearch',
                  'keyword': values.search_term,
                });
                applyFilters();
              }}
            >
              <InputSearch
                class="w-full big-searchbox"
                value={values.search_term}
                onChange={(e) => {
                  const value = e.target.value;
                  setValues({ ...values, search_term: value });
                }}
                onFocus={() => setIsSearchBoxFocused(true)}
                onBlur={() => setIsSearchBoxFocused(false)}
              />
              <p
                className={`text-desktopXS mt-2 !mb-0 opacity-50 ${isSearchBoxFocused ? "" : "invisiblexx"
                  }`}
              >
                {_getTranslation("press_enter_to_search")}
              </p>
            </form>

            <MobileFilters class="w-full lg:hidden">
              {getFilterInputs()}
            </MobileFilters>

            <FilterTags />
          </div>
        </div>
      </div>
      <div className="container my-8 md:my-12 xl:my-16 grid lg:flex gap-8 md:gap-x-12">
        <div className="hidden lg:block w-[330px]">
          <h4 className="h4">{_getTranslation("filter_by")}</h4>
          <div className="grid gap-2">{getFilterInputs()}</div>
        </div>
        <div className="flex-1 grid gap-y-8 lg:gap-y-16">
          {!areFiltersActive && (
            <section>
              <h4 className="h4 !mb-4">
                {_getTranslation("highlighted_publications")}
              </h4>
              {isLoadingPosts && <Loader />}
              {_.isArray(highlightedPosts) && highlightedPosts.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
                  {highlightedPosts.map((post) => (
                    <CardPublication data={post} />
                  ))}
                </div>
              ) : isLoadingPosts ? null : (
                <p className="text-center">{_getTranslation("there_are_no_posts_available")}.</p>
              )}
            </section>
          )}

          <section>
            <div ref={scrollRef} style={{ height: '1px' }}></div>

            {!areFiltersActive ? (
              <h4 className="h4 !mb-4">
                {_getTranslation("latest_publications")}
              </h4>
            ) : (
              <div className="flex items-start justify-end gap-4 mb-6">
                <label className="mt-4 text-sm">
                  {_getTranslation("sort_by")}
                </label>
                <InputSelect
                  name="sorting-order"
                  class="w-80 max-w-full"
                  options={SORTING_OPTIONS}
                  isSort
                  isSearchable
                  value={_findSelectOption(SORTING_OPTIONS, sortby)}
                  onChange={(name, selectedValues) => {
                    setSortby(selectedValues);
                  }}
                />
              </div>
            )}

            {isLoadingPosts && <Loader class="mb-8" />}
            {_.isArray(posts) && posts.length > 0 ? (
              <React.Fragment>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 mb-8">
                  {posts.map((post) => (
                    <CardPublication data={post} />
                  ))}
                </div>
                <div className="flex items-center justify-center gap-x-6 mt-8">
                  <Pagination
                    page={appliedFilters.page}
                    totalPages={totalPages}
                    setPage={(newPage) => {
                      if (areFiltersActive) {
                        applyFilters(newPage);
                      } else {
                        applyFilters(newPage, false);
                      }
                    }}
                  />
                  {isLoadingPosts && <Loader />}
                </div>
              </React.Fragment>
            ) : isLoadingPosts ? null : (
              <p className="text-center">{_getTranslation("there_are_no_posts_available")}.</p>
            )}
          </section>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Publications;
